import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });

  section.dataset.hydrated = 'true';
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * ym: the header is the exception. Its hydration only adds the desktop mega menu's featured products
 * (snippets/mega-menu-list.liquid renders them through the Section Rendering API), yet hydrating on idle
 * re-fetched the whole header (about 200 KB) and downloaded every menu product image on every page view,
 * including on phones, where the desktop menu is never shown. The header now hydrates the first time
 * someone points at, or tabs into, the desktop menu. The theme editor keeps the original behaviour.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    const section = document.getElementById(buildSectionSelector(normalizeSectionId(sectionId)));
    const menu = section?.querySelector('header-menu');

    if (!menu || window.Shopify?.designMode) {
      requestIdleCallback(() => hydrateSection(sectionId, url));
      return;
    }

    const start = () => {
      menu.removeEventListener('pointerenter', start);
      menu.removeEventListener('focusin', start);
      hydrateSection(sectionId, url);
    };

    menu.addEventListener('pointerenter', start);
    menu.addEventListener('focusin', start);
  });
}
